/**
 * Ask a question; "/" (or the Skills button) opens the skills that fit
 * where you are; the chosen skill shows as a chip until sent. Enter sends,
 * Shift+Enter makes a new line.
 */

import { useState, type KeyboardEvent, type ReactNode } from 'react';

import type { Skill } from './assistant-api.ts';

export function AssistantComposer(props: {
  skills: Skill[];
  inClient: boolean;
  busy: boolean;
  onSend: (text: string, skill: Skill | null) => void;
}): ReactNode {
  const [text, setText] = useState('');
  const [skill, setSkill] = useState<Skill | null>(null);
  const [menu, setMenu] = useState(false);
  const fitting = props.skills.filter(
    (item) => item.scope === 'ANY' || (item.scope === 'CLIENT') === props.inClient,
  );
  const send = () => {
    const question = text.trim() || (skill ? skill.name : '');
    if (!question || props.busy) return;
    props.onSend(question, skill);
    setText('');
    setSkill(null);
  };
  const onKey = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      send();
    }
  };
  return (
    <div className="as-composer">
      {menu && (
        <ul className="as-skills" role="listbox" aria-label="Skills">
          {fitting.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                role="option"
                aria-selected={skill?.id === item.id}
                onClick={() => {
                  setSkill(item);
                  setMenu(false);
                }}
              >
                <strong>{item.name}</strong>
                <small>{item.description || (item.built_in ? '' : 'Your skill')}</small>
              </button>
            </li>
          ))}
        </ul>
      )}
      {skill && (
        <p className="as-chip">
          Skill: {skill.name}
          <button
            type="button"
            className="ex-link"
            aria-label="Remove skill"
            onClick={() => setSkill(null)}
          >
            ×
          </button>
        </p>
      )}
      <div className="as-box">
        <label className="sr-only" htmlFor="as-input">
          Ask a question
        </label>
        <textarea
          id="as-input"
          rows={2}
          maxLength={8000}
          placeholder="Ask a question, or type / for skills"
          value={text}
          onKeyDown={onKey}
          onChange={(event) => {
            const value = event.target.value;
            if (value === '/') {
              setMenu(true);
              setText('');
              return;
            }
            setText(value);
          }}
        />
        <div className="as-composer-bar">
          <button
            type="button"
            className="as-skill-btn"
            aria-expanded={menu}
            onClick={() => setMenu(!menu)}
          >
            / Skills
          </button>
          <span className="ag-spacer" />
          <button
            type="button"
            className="as-send"
            aria-label="Send"
            disabled={props.busy || (!text.trim() && !skill)}
            onClick={send}
          >
            ↑
          </button>
        </div>
      </div>
    </div>
  );
}
